
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Copy, Heart } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

interface PartCardFooterProps {
  partId: number;
  onMoreInfoClick: () => void;
  externalOnHeartClick?: (partId: number) => void;
}

const PartCardFooter = ({ partId, onMoreInfoClick, externalOnHeartClick }: PartCardFooterProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isSaved, setIsSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Check if the item is already saved
  useEffect(() => {
    if (!user || !partId) return;
    
    const checkIfSaved = async () => {
      try {
        const { data, error } = await supabase
          .from('saved_items')
          .select()
          .eq('user_id', user.id)
          .eq('part_id', partId.toString());
          
        if (error) throw error;
        
        setIsSaved(data && data.length > 0);
      } catch (error) {
        console.error("Error checking saved status:", error);
      }
    };
    
    checkIfSaved();
  }, [user, partId]);
  
  const handleSave = async () => {
    if (!user) {
      // Redirect to auth page if not logged in
      navigate("/auth");
      return;
    }
    
    if (!partId) return;
    
    setIsLoading(true);
    try {
      if (isSaved) {
        // Remove from saved items
        const { error } = await supabase
          .from('saved_items')
          .delete()
          .eq('user_id', user.id)
          .eq('part_id', partId.toString());
          
        if (error) throw error;
        
        setIsSaved(false);
        toast.success("Removed from saved items");
        
        // Call external handler if provided (used in SavedItems page)
        if (externalOnHeartClick) {
          externalOnHeartClick(partId);
        }
      } else {
        // Add to saved items
        const { error } = await supabase
          .from('saved_items')
          .insert({
            user_id: user.id,
            part_id: partId.toString()
          });
          
        if (error) throw error;
        
        setIsSaved(true);
        toast.success("Added to saved items");
      }
    } catch (error) {
      console.error("Error updating saved status:", error);
      toast.error("Failed to update saved status");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = () => {
    const url = `${window.location.origin}/parts/${partId}`;
    navigator.clipboard.writeText(url)
      .then(() => toast.success("Link copied to clipboard"))
      .catch(() => toast.error("Failed to copy link"));
  };

  return (
    <div className="border-t flex justify-between items-center py-4 px-4 w-full">
      <Button 
        variant="outline" 
        size="sm" 
        className="py-1.5 whitespace-normal h-auto min-h-9 bg-accent hover:bg-accent/80 text-xs w-auto flex-1"
        onClick={onMoreInfoClick}
      >
        More Details
      </Button>
      
      <div className="flex gap-2 ml-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="outline" 
                size="icon" 
                className="flex items-center justify-center border-border"
                onClick={handleCopyLink}
              >
                <Copy className="h-4 w-4" />
                <span className="sr-only">Copy link</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Copy link</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="outline" 
                size="icon"
                className="flex items-center justify-center border-border"
                onClick={handleSave}
                disabled={isLoading}
              >
                <Heart 
                  className={`h-4 w-4 ${isSaved ? "text-red-500 fill-red-500" : ""}`} 
                />
                <span className="sr-only">{isSaved ? "Remove from saved items" : "Save for later"}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{isSaved ? "Remove from saved items" : "Save for later"}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};

export default PartCardFooter;
